import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AuthProvider } from './contexts/AuthContext';
import GlobalStyle from './styles/GlobalStyle';
import theme from './styles/theme';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import CommonLayout from './components/CommonLayout';
import { USER_TYPES } from './utils/userTypeUtils';

const CANDIDATE = [USER_TYPES.CANDIDATE];
const CONSULTANCY = [USER_TYPES.CONSULTANCY, USER_TYPES.ADMIN];
const INSTITUTION = [USER_TYPES.INSTITUTION, USER_TYPES.ADMIN];
const ANY_AUTH = [USER_TYPES.CANDIDATE, USER_TYPES.CONSULTANCY, USER_TYPES.INSTITUTION, USER_TYPES.PARTNER, USER_TYPES.ADMIN];
/** Candidate / consultancy / institution / admin — not EdTech partners */
const HIRING_AUTH = [USER_TYPES.CANDIDATE, USER_TYPES.CONSULTANCY, USER_TYPES.INSTITUTION, USER_TYPES.ADMIN];
const PARTNER = [USER_TYPES.PARTNER, USER_TYPES.ADMIN];
const ADMIN = [USER_TYPES.ADMIN];

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import SetPassword from './pages/SetPassword';

import NotFound from './pages/NotFound';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Settings = lazy(() => import('./pages/Settings'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const Interviews = lazy(() => import('./pages/Interviews'));
const JobDescriptions = lazy(() => import('./pages/JobDescriptions'));
const InterviewSchedules = lazy(() => import('./pages/InterviewSchedules'));
const ScheduleInterview = lazy(() => import('./pages/ScheduleInterview'));
const MockInterviews = lazy(() => import('./pages/MockInterviews'));
const InterviewPlanner = lazy(() => import('./pages/InterviewPlanner'));
const InterviewPlanDetail = lazy(() => import('./pages/InterviewPlanDetail'));
const InterviewPlanReport = lazy(() => import('./pages/InterviewPlanReport'));
const InterviewHistory = lazy(() => import('./pages/InterviewHistory'));
const PlanRoundRunner = lazy(() => import('./pages/PlanRoundRunner'));
const AnswerReview = lazy(() => import('./pages/AnswerReview'));
const OpeningPipeline = lazy(() => import('./pages/OpeningPipeline'));
const PublicDossier = lazy(() => import('./pages/PublicDossier'));
const PublicBadge = lazy(() => import('./pages/PublicBadge'));
const InstitutionDrives = lazy(() => import('./pages/InstitutionDrives'));
const InstitutionTrainings = lazy(() => import('./pages/InstitutionTrainings'));
const MyTrainings = lazy(() => import('./pages/MyTrainings'));
const AdminInstitutionTrainings = lazy(() => import('./pages/AdminInstitutionTrainings'));
const AdminTrainingAdvertise = lazy(() => import('./pages/AdminTrainingAdvertise'));
const PartnerTeam = lazy(() => import('./pages/PartnerTeam'));
const Analytics = lazy(() => import('./pages/Analytics'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const HelpSupport = lazy(() => import('./pages/HelpSupport'));
const Tutorials = lazy(() => import('./pages/Tutorials'));
const Partners = lazy(() => import('./pages/Partners'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const AdminPartners = lazy(() => import('./pages/AdminPartners'));
const AdminSales = lazy(() => import('./pages/AdminSales'));
const AdminWesenseU = lazy(() => import('./pages/AdminWesenseU'));
const AdminWesenseUConnection = lazy(() => import('./pages/AdminWesenseUConnection'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Features = lazy(() => import('./pages/Features'));
const About = lazy(() => import('./pages/About'));
const Demo = lazy(() => import('./pages/Demo'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminSubscriptions = lazy(() => import('./pages/AdminSubscriptions'));
const SkillAssessment = lazy(() => import('./pages/SkillAssessment'));
const CareerPath = lazy(() => import('./pages/CareerPath'));
const Performance = lazy(() => import('./pages/Performance'));
const Preparation = lazy(() => import('./pages/Preparation'));
const JobMatching = lazy(() => import('./pages/JobMatching'));
const InterviewModes = lazy(() => import('./pages/InterviewModes'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminCrm = lazy(() => import('./pages/AdminCrm'));
const AdminStaffUsers = lazy(() => import('./pages/AdminStaffUsers'));
const AdminTestManager = lazy(() => import('./pages/AdminTestManager'));
const ConsultancyCrmBoard = lazy(() => import('./pages/ConsultancyCrmBoard'));
const ConsultancyTeam = lazy(() => import('./pages/ConsultancyTeam'));
const SystemMonitoring = lazy(() => import('./pages/SystemMonitoring'));
const AdminSupport = lazy(() => import('./pages/AdminSupport'));
const AdminSecurity = lazy(() => import('./pages/AdminSecurity'));
const AdminApiOps = lazy(() => import('./pages/AdminSecurity').then((m) => ({ default: m.AdminApiOps })));
const AdminKnowledge = lazy(() => import('./pages/AdminKnowledge'));
const ContentManagement = lazy(() => import('./pages/ContentManagement'));
const PaymentGatewayManagement = lazy(() => import('./pages/PaymentGatewayManagement'));
const SystemSettings = lazy(() => import('./pages/SystemSettings'));
const CandidateManagement = lazy(() => import('./pages/CandidateManagement'));
const ClientCompanies = lazy(() => import('./pages/ClientCompanies'));
const OpeningsDesk = lazy(() => import('./pages/OpeningsDesk'));
const CoBrokerBoard = lazy(() => import('./pages/CoBrokerBoard'));
const ReplacementsDesk = lazy(() => import('./pages/ReplacementsDesk'));
const InstitutionTeam = lazy(() => import('./pages/InstitutionTeam'));
const InterviewScheduling = lazy(() => import('./pages/InterviewScheduling'));
const PlacementAnalytics = lazy(() => import('./pages/PlacementAnalytics'));
const CandidateReports = lazy(() => import('./pages/CandidateReports'));
const ConsultancyOps = lazy(() => import('./pages/ConsultancyOps'));
const BulkOperations = lazy(() => import('./pages/BulkOperations'));
const BusinessIntelligence = lazy(() => import('./pages/BusinessIntelligence'));
const SubscriptionBilling = lazy(() => import('./pages/SubscriptionBilling'));
const Support = lazy(() => import('./pages/Support'));
const TalentDiscovery = lazy(() => import('./pages/TalentDiscovery'));
const InvitesInbox = lazy(() => import('./pages/InvitesInbox'));
const InstitutionDashboard = lazy(() => import('./pages/InstitutionDashboard'));
const StudentCurriculum = lazy(() => import('./pages/StudentCurriculum'));
const InterviewRecord = lazy(() => import('./pages/InterviewRecord'));
const AddSubscription = lazy(() => import('./pages/AddSubscription'));
const SubscriptionDetails = lazy(() => import('./pages/SubscriptionDetails'));
const EditSubscription = lazy(() => import('./pages/EditSubscription'));
const AdminInterviewHierarchy = lazy(() => import('./pages/AdminInterviewHierarchy'));
const AdminQuestionBank = lazy(() => import('./pages/AdminQuestionBank'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));

// Consultancy Pages

// Additional Pages

// Admin Components

// Registration Pages
// import RegisterCandidate from './pages/RegisterCandidate';
// import RegisterConsultancy from './pages/RegisterConsultancy';

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <AuthProvider>
          <div className="App">
            <Suspense fallback={<div role="status" style={{padding:"2rem",textAlign:"center"}}>Loading…</div>}>
            <Routes>
              {/* Public Routes with CommonLayout */}
              <Route path="/" element={<CommonLayout><Home /></CommonLayout>} />
              <Route path="/login" element={<CommonLayout><Login /></CommonLayout>} />
              <Route path="/register" element={<CommonLayout><Register /></CommonLayout>} />
              <Route path="/register/:userType" element={<CommonLayout><Register /></CommonLayout>} />
              <Route path="/register/candidate" element={<CommonLayout><Register /></CommonLayout>} />
              <Route path="/register/consultancy" element={<CommonLayout><Register /></CommonLayout>} />
              <Route path="/register/institution" element={<CommonLayout><Register /></CommonLayout>} />
              <Route path="/register/partner" element={<CommonLayout><Register /></CommonLayout>} />
              <Route path="/verify-email" element={<CommonLayout><VerifyEmail /></CommonLayout>} />
              <Route path="/set-password" element={<CommonLayout><SetPassword /></CommonLayout>} />
              <Route path="/verify-email/:token" element={<CommonLayout><VerifyEmail /></CommonLayout>} />
              <Route path="/pricing" element={<CommonLayout><Pricing /></CommonLayout>} />
              <Route path="/features" element={<CommonLayout><Features /></CommonLayout>} />
              <Route path="/about" element={<CommonLayout><About /></CommonLayout>} />
              <Route path="/demo" element={<CommonLayout><Demo /></CommonLayout>} />
              <Route path="/help-support" element={<CommonLayout><HelpSupport /></CommonLayout>} />
              <Route path="/tutorials" element={<CommonLayout><Tutorials /></CommonLayout>} />
              <Route path="/partners" element={<CommonLayout><Partners /></CommonLayout>} />
              <Route path="/dossier/:token" element={<PublicDossier />} />
              <Route path="/badge/:token" element={<PublicBadge />} />

              {/* Protected Routes with Sidebar Layout */}
              <Route path="/dashboard" element={
                <ProtectedRoute allowedUserTypes={ANY_AUTH}>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute allowedUserTypes={ANY_AUTH}>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/portfolio" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <Portfolio />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute allowedUserTypes={ANY_AUTH}>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/subscriptions" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <Subscriptions />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interviews" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <Interviews />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interviews/schedule" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <MockInterviews />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/mock-interviews" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <MockInterviews />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-history" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <InterviewHistory />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-plans/new" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <InterviewPlanner />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-plans/:planId/report" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <InterviewPlanReport />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-plans/:planId" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <InterviewPlanDetail />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/review-answers" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <AnswerReview />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-plans/:planId/rounds/:roundNumber" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <PlanRoundRunner />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/mock-interviews/schedule" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <MockInterviews />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/skill-assessment" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <SkillAssessment />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/interview-modes" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <InterviewModes />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/career-path" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <CareerPath />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/performance" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <Performance />
                </Layout>
                </ProtectedRoute>
              } />
              <Route path="/preparation" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <Preparation />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/job-matching" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <JobMatching />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/job-descriptions" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <JobDescriptions />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/job-descriptions/upload" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <JobDescriptions />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-schedules" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <InterviewSchedules />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/schedule-interview" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <ScheduleInterview />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/subscriptions" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminSubscriptions />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/subscriptions/add" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AddSubscription />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/subscriptions/:id" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <SubscriptionDetails />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/subscriptions/:id/edit" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <EditSubscription />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Consultancy Routes */}
              <Route path="/invites" element={
                <ProtectedRoute allowedUserTypes={ANY_AUTH}>
                  <Layout>
                    <InvitesInbox />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/learners" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/profile" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/plans" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/reports" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/interviews" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/trainings" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionTrainings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/my-trainings" element={
                <ProtectedRoute allowedUserTypes={CANDIDATE}>
                  <Layout>
                    <MyTrainings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/drives" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDrives />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/team" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionTeam />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/institution/curriculum" element={
                <ProtectedRoute allowedUserTypes={INSTITUTION}>
                  <Layout>
                    <InstitutionDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/profile/curriculum" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <StudentCurriculum />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interviews/:interviewId/report" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <InterviewRecord />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interviews/:interviewId/questions" element={
                <ProtectedRoute allowedUserTypes={HIRING_AUTH}>
                  <Layout>
                    <InterviewRecord />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/talent-discovery" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <TalentDiscovery />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/candidate-management" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CandidateManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/crm" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <ConsultancyCrmBoard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/team" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <ConsultancyTeam />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/client-companies" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <ClientCompanies />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/openings" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <OpeningsDesk />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/cobroker" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CoBrokerBoard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/replacements" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <ReplacementsDesk />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/openings/:requirementId" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <OpeningPipeline />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/interview-scheduling" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <InterviewScheduling />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/placement-analytics" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <PlacementAnalytics />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/candidate-reports" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CandidateReports />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/consultancy-ops" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <ConsultancyOps />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/bulk-operations" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <BulkOperations />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/subscription-billing" element={
                <ProtectedRoute allowedUserTypes={ANY_AUTH}>
                  <Layout>
                    <SubscriptionBilling />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Additional Routes */}
              <Route path="/business-intelligence" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <BusinessIntelligence />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/support" element={
                <ProtectedRoute allowedUserTypes={ANY_AUTH}>
                  <Layout>
                    <Support />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/crm" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminCrm />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/staff" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminStaffUsers />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/test-manager" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminTestManager />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/dashboard" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/panel" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminPanel />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/knowledge" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminKnowledge />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin-dashboard" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/content" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <ContentManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/partners" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminPartners />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/institution-trainings" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminInstitutionTrainings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/training-advertise" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminTrainingAdvertise />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/partner/team" element={
                <ProtectedRoute allowedUserTypes={PARTNER}>
                  <Layout>
                    <PartnerTeam />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/sales" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly>
                  <Layout>
                    <AdminSales />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/wesenseu" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminWesenseU />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/wesenseu-connection" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminWesenseUConnection />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/partner/dashboard" element={
                <ProtectedRoute allowedUserTypes={PARTNER}>
                  <Layout>
                    <PartnerDashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/payment-gateways" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <PaymentGatewayManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/payment-gateway" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <PaymentGatewayManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/analytics" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminAnalytics />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/interview-hierarchy" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminInterviewHierarchy />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/admin/question-bank" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminQuestionBank />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/monitoring" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <SystemMonitoring />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/metrics" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <SystemMonitoring />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/security" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminSecurity />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/api" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminApiOps />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/user-management" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/content-management" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <ContentManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/admin/support" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <AdminSupport />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/system-settings" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <SystemSettings />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute allowedUserTypes={ADMIN} adminOnly blockSalesLimited>
                  <Layout>
                    <SystemSettings />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/candidate/add" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CandidateManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/candidate/:id/profile" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CandidateManagement />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/candidate/:id/report" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CandidateReports />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/candidate/:id/edit" element={
                <ProtectedRoute allowedUserTypes={CONSULTANCY}>
                  <Layout>
                    <CandidateManagement />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* 404 Route */}
              <Route path="*" element={<CommonLayout><NotFound /></CommonLayout>} />
            </Routes>
            </Suspense>
          </div>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App; 